import { InfoCircle } from '@untitled-ui/icons-react/build/cjs';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import React, { useState, useMemo, useEffect } from 'react';
import { Controller, Control, FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ActionTooltip from 'src/components/ActionTooltip';
import CardWithOptions from 'src/components/CardWithOptions';
import SelectBox from 'src/components/SelectBox/SelectBox';
import useGoogleMaps from 'src/hooks/useGoogleMaps';
import { useAppSelector } from 'src/redux/hooks';
import { RootState } from 'src/redux/store';

interface IAddress {
    fullAddress?: string;
    address1?: string;
    city?: string;
    state?: string;
    stateCode?: string;
    country?: string;
    countryCode?: string;
    postal?: string;
    latitude?: string;
    longitude?: string;
    streetNumber?: string;
    timezone?: string;
    additionalServiceIds?: number[];
}

interface IOption {
    label: string;
    value: string | number;
}

interface ShipperConsigneeAutocompleteProps {
    control: Control<any>;
    isShipper: boolean;
    isEditId: boolean;
    watchCompanyName: string;
    watchShipperAddress: IAddress | null;
    watchShipperFullAddress: string | undefined;
    shipperOptions: IOption[];
    consigneeOptions: IOption[];
    errors: FieldErrors<any>;
    handleShipperChange: (onChange: any) => (value: any) => void;
    handleCreateNew: (data?: any) => void;
    handleCardActionType: (value: string) => () => void;
    onInputChange?: (e: any, inputAction: any) => void;
    fieldName?: string;
    isAddressLoading?: boolean;
    showDeleteOption?: boolean;
    isQuoteCreateForm?: boolean;
    handleGoogleAddressSelect?: (addressData: any) => void;
    watchShipperConsigneeId?: number | null;
    showCardOptions?: boolean;
    // Stop steps reuse this card for a generic stop address (a stop can be pickup, delivery or
    // both), so the shipper/consignee wording does not apply there — use neutral "Address" copy.
    isOpenFromStop?: boolean;
}

// Helper function to check nearby address (defined first since parseGooglePlaceDetails uses it)
const checkNearByAddress = async (lat: number, lng: number, googleKey: string, retryCount = 0, isRecheckAddress = false): Promise<any> => {
    const response = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${googleKey}`).catch((error) => {
        console.error('Failed to fetch geocode data:', error);
        throw new Error('Network error');
    });

    const details = {
        postalCode: '',
        city: '',
        state: '',
        country: '',
        address: '',
    };

    if (response.ok) {
        const data = await response.json();

        if (data.status === 'OK') {
            data.results.forEach((result: any) => {
                result.address_components.forEach((component: any) => {
                    switch (true) {
                        case component.types.includes('postal_code'):
                            details.postalCode = component.long_name.length > 3 ? component.long_name : details.postalCode;
                            break;
                        case component.types.includes('locality'):
                            details.city = component.long_name;
                            break;
                        case component.types.includes('administrative_area_level_1'):
                            details.state = component.long_name;
                            break;
                        case component.types.includes('country'):
                            details.country = component.long_name;
                            break;
                        default:
                            break;
                    }
                });
            });
        }
    }

    if (!details.address) {
        details.address = details.city;
    }

    if ((!details.address || !details.city || !details.postalCode || !details.state || !details.country || details.postalCode?.length < 4) && retryCount < 3 && !isRecheckAddress) {
        const newLat = lat + (Math.random() - 0.5) * 0.01;
        const newLng = lng + (Math.random() - 0.5) * 0.01;

        return checkNearByAddress(newLat, newLng, googleKey, retryCount + 1, true);
    }

    return details;
};

// Helper function to parse Google Place details
const parseGooglePlaceDetails = async (place: any, googleKey: string) => {
    if (!place || !place.address_components) {
        return null;
    }

    const addressObject = place.address_components;
    let latitude = place?.geometry?.location.lat();
    let longitude = place?.geometry?.location.lng();

    // Handle if lat/lng are functions
    if (typeof latitude === 'function') latitude = latitude();
    if (typeof longitude === 'function') longitude = longitude();

    const fullAddress = place.formatted_address;
    let city = '';
    let state = '';
    let postcode = '';
    let addressOne = '';
    let country = '';
    let stateCode = '';
    let countryCode = '';
    let companyName = '';
    let isCity = false;
    let phoneNumber = null;
    let streetNumber = '';

    if (place.types?.includes('establishment')) {
        companyName = place.name;
    }

    addressObject.forEach((component: any) => {
        const componentType = component.types;

        if (componentType.includes('premise')) {
            addressOne += `${component.long_name} `;
        } else if (componentType.includes('street_number')) {
            streetNumber = component.long_name;
        } else if (componentType.includes('route')) {
            addressOne += `${component.short_name} `;
        } else if (componentType.includes('sublocality')) {
            addressOne += `${component.long_name} `;
        } else if (componentType.includes('postal_code')) {
            postcode = `${component.long_name}${postcode}`;
        } else if (componentType.includes('locality')) {
            city = component.long_name;
            isCity = true;
        } else if (componentType.includes('administrative_area_level_1')) {
            state = component.long_name;
            stateCode = component.short_name;
        } else if (componentType.includes('country')) {
            country = component.long_name;
            countryCode = component.short_name;
        }

        if (!isCity && (componentType.includes('sublocality_level_1') || componentType.includes('sublocality'))) {
            city = component.long_name;
        }
    });

    let extraAddress;
    let address1;

    if (addressOne) {
        extraAddress = fullAddress.substring(0, fullAddress.indexOf(addressOne.split(' ')[0]));
        address1 = `${extraAddress} ${addressOne}`;
    } else {
        address1 = city;
    }

    // Check nearby address if missing required fields
    if (!address1 || !city || !postcode || !state || !country || postcode.length < 4) {
        const addressDetails: any = await checkNearByAddress(latitude, longitude, googleKey);

        if (!address1) {
            address1 = addressDetails.address;
        }

        if (!city) {
            city = addressDetails.city;
        }

        if (!postcode || postcode.length < 4) {
            postcode = addressDetails.postalCode;
        }

        if (!state) {
            state = addressDetails.state;
        }

        if (!country) {
            country = addressDetails.country;
        }
    }

    if (place.formatted_phone_number) {
        try {
            phoneNumber = parsePhoneNumberFromString(`+1${place.formatted_phone_number}`)?.number;
        } catch (error) {
            console.error('Error parsing phone number:', error);
        }
    }

    if (address1 && streetNumber) {
        address1 = address1.replace(streetNumber, '');
    }

    return {
        fullAddress,
        address1: address1?.trim() || '',
        city,
        state,
        country,
        postal: postcode,
        latitude,
        longitude,
        stateCode,
        countryCode,
        companyName,
        phoneNumber,
        streetNumber,
    };
};

const ShipperConsigneeAutocomplete = ({
    control,
    isShipper,
    isEditId,
    watchCompanyName,
    watchShipperAddress,
    watchShipperFullAddress,
    shipperOptions,
    consigneeOptions,
    errors,
    handleShipperChange,
    handleCreateNew,
    handleCardActionType,
    onInputChange,
    fieldName = 'id',
    isAddressLoading = false,
    showDeleteOption = true,
    isQuoteCreateForm = false,
    handleGoogleAddressSelect,
    watchShipperConsigneeId,
    showCardOptions = true,
    isOpenFromStop = false,
}: ShipperConsigneeAutocompleteProps) => {
    const { t } = useTranslation();
    const { googleKey } = useAppSelector((state: RootState) => state.basic);
    // google.maps is used inside the typing/selection handlers below, so the script only needs to
    // be requested on mount — there is no init effect to re-run.
    useGoogleMaps();
    const { shippers, consignees } = useAppSelector((state: RootState) => state.addresses);
    const [addressOptions, setAddressOptions] = useState<any[]>([]);
    const [searchInput, setSearchInput] = useState<string>('');

    // Clear address options when address is cleared (e.g., when switching)
    useEffect(() => {
        if (!watchShipperAddress) {
            setAddressOptions([]);
            setSearchInput('');

            if (onInputChange) {
                onInputChange('', { action: 'input-clear' });
            }
        }
    }, [watchShipperAddress]);

    const formattedShipperOptions = useMemo(
        () =>
            shipperOptions.map((option: any) => {
                const shipper = shippers?.find((s: any) => s.id === option.value);
                const address = shipper?.fullAddress || '';

                return {
                    ...option,
                    label: option.label,
                    subLabel: address || undefined,
                };
            }),
        [shipperOptions, shippers]
    );

    const formattedConsigneeOptions = useMemo(
        () =>
            (consigneeOptions || []).map((option: any) => {
                const consignee = consignees?.find((c: any) => c.id === option.value);
                const address = consignee?.fullAddress || '';

                return {
                    ...option,
                    label: option.label,
                    subLabel: address || undefined,
                };
            }),
        [consigneeOptions, consignees]
    );

    if (isEditId) {
        return null;
    }

    const hasAddressError = isQuoteCreateForm
        ? isShipper
            ? !!(errors.shipperCompanyName || errors.shipperAddress)
            : !!(errors.consigneeCompanyName || errors.consigneeAddress || errors.shipperAddress)
        : isShipper
        ? !!(errors.shipperFullAddress || errors.shipperLatitude || errors.shipperLongitude || errors.shipperCity || errors.shipperState || errors.shipperCountry)
        : !!(errors.consigneeFullAddress || errors.consigneeLatitude || errors.consigneeLongitude || errors.consigneeCity || errors.consigneeState || errors.consigneeCountry);

    const fetchAddress = (inputValue: string) => {
        if (!inputValue || !window.google || !window.google.maps) return;

        const autocompleteService = new window.google.maps.places.AutocompleteService();
        autocompleteService.getPlacePredictions(
            {
                input: inputValue,
                componentRestrictions: { country: ['us', 'ca'] },
            },
            (predictions: any, status: any) => {
                if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
                    const options = predictions.map((prediction: any) => ({
                        label: prediction.description,
                        value: prediction.place_id,
                        isAddress: true,
                        description: prediction.description,
                    }));
                    setAddressOptions(options);
                } else {
                    setAddressOptions([]);
                }
            }
        );
    };

    const handleInputChange = (inputValue: string, actionMeta: any) => {
        if (onInputChange) {
            onInputChange(inputValue, actionMeta);
        }

        // Track search input value
        setSearchInput(inputValue || '');

        if (actionMeta.action === 'input-change' && inputValue) {
            fetchAddress(inputValue);
        } else {
            setAddressOptions([]);
        }
    };

    const handleMenuClose = () => {
        setAddressOptions([]);
        setSearchInput('');
    };

    const handleOnChange = (onChange: any) => async (selectedOption: any) => {
        if (selectedOption?.isAddress) {
            if (!window.google || !window.google.maps) return;
            const placesService = new window.google.maps.places.PlacesService(document.createElement('div'));

            placesService.getDetails({ placeId: selectedOption.value }, async (place: any, status: any) => {
                if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                    const addressData = await parseGooglePlaceDetails(place, googleKey);

                    if (addressData) {
                        // For quote form, set address directly without creating new shipper/consignee
                        if (handleGoogleAddressSelect) {
                            handleGoogleAddressSelect(addressData);
                        } else {
                            // For order form, use the create handler
                            handleCreateNew(addressData);
                        }
                    }
                }
            });
        } else {
            handleShipperChange(onChange)(selectedOption);
        }
    };

    return (
        <>
            {!watchShipperAddress ? (
                <Controller
                    name={fieldName}
                    control={control}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <SelectBox
                            truncateToOneLine
                            parentClassName="w-full "
                            selectoptiontext="!mr-1"
                            labelClassName="form_label block mb-1.5"
                            SelectDropdownWrapClass="select-box-shipper-consignee-group-wrap"
                            name={fieldName}
                            id={fieldName}
                            className={`form_control`}
                            placeholder={isOpenFromStop ? t('Orders.Find_or_add_address') : isShipper ? t('Orders.Find_or_add_shipper') : t('Orders.Find_or_add_consignee')}
                            isSearchable={true}
                            isClearable={true}
                            options={[
                                {
                                    label: searchInput ? (isShipper ? 'Saved Shippers' : 'Saved Consignees') : 'Recent',
                                    options: isShipper ? formattedShipperOptions : formattedConsigneeOptions,
                                },
                                {
                                    label: 'Address List',
                                    options: addressOptions,
                                },
                            ]}
                            isNewOption={true}
                            isCloseOnScroll={true}
                            newOptionText={isOpenFromStop ? t('Orders.Add_New_Address') : isShipper ? t('Orders.Add_New_Shipper') : t('Orders.Add_New_Consignee')}
                            handleCreate={handleCreateNew}
                            onInputChange={handleInputChange}
                            onMenuClose={handleMenuClose}
                            onChangeFunc={handleOnChange(onChange)}
                            defaultValueInput=""
                            value={
                                value
                                    ? isShipper
                                        ? formattedShipperOptions.find((shipper: any) => shipper.value === value)
                                        : formattedConsigneeOptions.find((consignee: any) => consignee.value === value)
                                    : null
                            }
                            isError={!!error?.message || hasAddressError}
                        />
                    )}
                />
            ) : (
                <CardWithOptions
                    handleActionType={handleCardActionType}
                    isLoading={isAddressLoading}
                    isError={hasAddressError}
                    rightAlignOffset={true}
                    tooltipContentFitWidth
                    showOptions={showCardOptions}
                    options={
                        // If it's a quote form and no shipper/consignee ID, show switch and add options
                        !watchShipperConsigneeId
                            ? [
                                  {
                                      label: t(isOpenFromStop ? 'Orders.Switch_Address' : isShipper ? 'Orders.Switch_Shipper' : 'Orders.Switch_Consignee'),
                                      value: 'switch_id',
                                  },
                                  {
                                      label: t(isOpenFromStop ? 'Orders.Add_New_Address' : isShipper ? 'Orders.Add_New_Shipper' : 'Orders.Add_New_Consignee'),
                                      value: 'edit_id',
                                  },
                              ]
                            : [
                                  {
                                      label: t(isOpenFromStop ? 'Orders.Switch_Address' : isShipper ? 'Orders.Switch_Shipper' : 'Orders.Switch_Consignee'),
                                      value: 'switch_id',
                                  },
                                  {
                                      label: t(
                                          isOpenFromStop
                                              ? !showDeleteOption
                                                  ? 'Orders.Add_New_Address'
                                                  : 'Orders.Edit_Address_Information'
                                              : isShipper
                                              ? !showDeleteOption
                                                  ? 'Orders.Add_New_Shipper'
                                                  : 'Orders.Edit_Shipper_Information'
                                              : !showDeleteOption
                                              ? 'Orders.Add_New_Consignee'
                                              : 'Orders.Edit_Consignee_Information'
                                      ),
                                      value: 'edit_id',
                                  },
                                  ...(showDeleteOption
                                      ? [
                                            {
                                                label: t(isOpenFromStop ? 'Orders.Delete_Address' : isShipper ? 'Orders.Delete_Shipper_Information' : 'Orders.Delete_Consignee_Information'),
                                                value: 'delete_id',
                                            },
                                        ]
                                      : []),
                              ]
                    }
                    headerContent={
                        <>
                            <div className="flex items-start gap-2">
                                <div className="flex-1">
                                    <h6 className="text-grayLight900 text-sm font-medium">
                                        {watchCompanyName ? watchCompanyName : isShipper ? t('Orders.Pickup_Address') : t('Orders.Delivery_Address')}
                                    </h6>

                                    <p className="text-grayLight600 text-xs leading-[1.5] font-normal flex gap-1">
                                        {watchShipperFullAddress}

                                        {errors?.addressMismatch?.message && (
                                            <ActionTooltip
                                                message={errors.addressMismatch.message}
                                                isOpenChildTopPosOnTop={true}
                                                openOnHover={true}
                                                parentClassName="max-w-[320px] min-w-[320px]"
                                                type="light"
                                            >
                                                <InfoCircle id="info-circle" className="text-red-500 h-4 w-4 cursor-pointer flex-shrink-0" />
                                            </ActionTooltip>
                                        )}
                                    </p>
                                </div>
                            </div>
                        </>
                    }
                />
            )}
        </>
    );
};

export default ShipperConsigneeAutocomplete;
